import { defineMessage } from 'react-intl';
import { faCreditCard } from '@fortawesome/pro-light-svg-icons/faCreditCard';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import { faIdCard } from '@fortawesome/pro-light-svg-icons/faIdCard';
import { faBell } from '@fortawesome/pro-light-svg-icons/faBell';
import { faListCheck } from '@fortawesome/pro-light-svg-icons/faListCheck';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { faCode } from '@fortawesome/pro-light-svg-icons/faCode';
import { faFileLines } from '@fortawesome/pro-light-svg-icons/faFileLines';
import { faCircleQuestion } from '@fortawesome/pro-light-svg-icons/faCircleQuestion';
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faTowerBroadcast } from '@fortawesome/pro-light-svg-icons/faTowerBroadcast';
import { SMLSettingsTabDef, SMLSettingsTabId } from './types';
import { accountMessages } from './account/accountMessages';
import { artistSettingsMessages } from './artist/artistSettingsMessages';
import { notificationsMessages } from './notifications/notificationsMessages';
import { supportMessages } from './support/supportMessages';
import { legalMessages } from './legal/legalMessages';
import { settingsTabHeadMessages } from './settingsTabHeadMessages';
import { mailboxMessages } from './outreach/mailboxMessages';
import { aiAssistantsMessages } from './aiAssistants/aiAssistantsMessages';

/**
 * The Settings tabs in rail order (design settings-redesign a1, owner decision 2026-10-03):
 * Account, Artist, SmartLinks | Subscription, Team, Connections | Notifications, Outreach, Setup, AI assistants,
 * API | Support, Legal. AI assistants came on 2026-10-11 (design ai-assistants a1).
 * The page head shows the title (the label, unless `title` says otherwise) and the subtitle of
 * the open tab, so a tab body draws no title of its own.
 */
export const smlSettingsTabs: SMLSettingsTabDef[] = [
  {
    id: SMLSettingsTabId.account,
    label: defineMessage({
      id: 'app.sml.settings.tab.account',
      defaultMessage: 'Account',
    }),
    subtitle: accountMessages.subtitle,
    icon: faUser,
    block: 'you',
  },
  // The active artist's avatar and name, then its page address and trackers (design artist-settings a1, b1).
  {
    id: SMLSettingsTabId.artist,
    label: defineMessage({
      id: 'app.sml.settings.tab.artist',
      defaultMessage: 'Artist',
    }),
    subtitle: artistSettingsMessages.artistSubtitle,
    icon: faIdCard,
    block: 'you',
  },
  {
    id: SMLSettingsTabId.smartlinks,
    label: defineMessage({
      id: 'app.sml.settings.tab.smartlinks',
      defaultMessage: 'SmartLinks',
    }),
    subtitle: artistSettingsMessages.smartLinksSubtitle,
    icon: faLink,
    block: 'you',
  },
  {
    id: SMLSettingsTabId.subscription,
    label: defineMessage({
      id: 'app.sml.settings.tab.subscription',
      defaultMessage: 'Subscription',
    }),
    subtitle: settingsTabHeadMessages.subscriptionSubtitle,
    icon: faCreditCard,
    block: 'workspace',
  },
  {
    id: SMLSettingsTabId.team,
    label: defineMessage({
      id: 'app.sml.settings.tab.team',
      defaultMessage: 'Team',
    }),
    subtitle: settingsTabHeadMessages.teamSubtitle,
    icon: faUsers,
    block: 'workspace',
  },
  // Navigation v2 (design f 6b): Channels moved here as Connections.
  {
    id: SMLSettingsTabId.connections,
    label: settingsTabHeadMessages.connectionsLabel,
    subtitle: settingsTabHeadMessages.connectionsSubtitle,
    icon: faTowerBroadcast,
    block: 'workspace',
  },
  {
    id: SMLSettingsTabId.notifications,
    label: defineMessage({
      id: 'app.sml.settings.tab.notifications',
      defaultMessage: 'Notifications',
    }),
    subtitle: notificationsMessages.subtitle,
    icon: faBell,
    block: 'product',
  },
  {
    // design: outreach i1 — Settings → Outreach → Mailboxes.
    id: SMLSettingsTabId.outreach,
    label: mailboxMessages.tabLabel,
    subtitle: mailboxMessages.tabSubtitle,
    icon: faPaperPlane,
    block: 'product',
  },
  {
    id: SMLSettingsTabId.setup,
    label: defineMessage({
      id: 'app.sml.settings.tab.setup',
      defaultMessage: 'Setup',
    }),
    subtitle: settingsTabHeadMessages.setupSubtitle,
    icon: faListCheck,
    block: 'product',
  },
  {
    id: SMLSettingsTabId.aiAssistants,
    label: aiAssistantsMessages.tab,
    subtitle: aiAssistantsMessages.subtitle,
    icon: faSparkles,
    block: 'product',
  },
  {
    id: SMLSettingsTabId.api,
    label: defineMessage({
      id: 'app.sml.settings.tab.api',
      defaultMessage: 'API',
    }),
    title: settingsTabHeadMessages.apiTitle,
    subtitle: settingsTabHeadMessages.apiSubtitle,
    icon: faCode,
    block: 'product',
  },
  {
    id: SMLSettingsTabId.support,
    label: defineMessage({
      id: 'app.sml.settings.tab.support',
      defaultMessage: 'Support',
    }),
    subtitle: supportMessages.subtitle,
    icon: faCircleQuestion,
    block: 'help',
  },
  {
    id: SMLSettingsTabId.legal,
    label: defineMessage({
      id: 'app.sml.settings.tab.legal',
      defaultMessage: 'Legal',
    }),
    subtitle: legalMessages.subtitle,
    icon: faFileLines,
    block: 'help',
  },
];
